import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Radio, Copy, Square, Loader2, Zap, Scissors } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { QuickClipAccess, useClipKey } from "@/components/QuickClipAccess";
import { feedback } from "@/lib/feedback";
import { clipErr } from "@/lib/clipErrors";

export const CONNECT_PLATFORMS = [
  { id: "twitch", name: "Twitch", color: "#9146ff", live: true, ph: "https://twitch.tv/ton_pseudo" },
  { id: "kick", name: "Kick", color: "#53fc18", live: true, ph: "https://kick.com/ton_pseudo" },
  { id: "youtube", name: "YouTube Live", color: "#ff0033", live: true, ph: "https://youtube.com/@toi/live" },
  { id: "tiktok", name: "TikTok LIVE", color: "#25f4ee", live: true, ph: "https://tiktok.com/@toi/live" },
  { id: "facebook", name: "Facebook Gaming", color: "#1877f2", live: true, ph: "https://facebook.com/toi/live" },
  { id: "trovo", name: "Trovo", color: "#19d66b", live: true, ph: "https://trovo.live/s/toi" },
  { id: "rumble", name: "Rumble", color: "#85c742", live: true, ph: "https://rumble.com/c/toi/live" },
  { id: "obs", name: "OBS Studio", color: "#c9d1e0", live: false },
  { id: "streamlabs", name: "Streamlabs", color: "#80f5d2", live: false },
  { id: "tiktok_studio", name: "TikTok LIVE Studio", color: "#fe2c55", live: false },
  { id: "xsplit", name: "XSplit · Meld · vMix", color: "#3ad7ff", live: false },
];

const Row = ({ value, testid, onCopy }) => (
  <div className="flex gap-2">
    <input readOnly value={value} className="flex-1 min-w-0 rounded-lg bg-[#070912] border border-white/10 px-3 py-2.5 text-xs font-mono text-[#3ad7ff]" data-testid={testid} />
    <button onClick={onCopy} className="cx-ghost rounded-lg px-3" data-testid={`${testid}-copy`}><Copy className="w-4 h-4" /></button>
  </div>
);

const QTYS = [10, 20, 30, 40];

const QtyPicker = ({ value, onPick, testid }) => (
  <div className="flex gap-2" data-testid={testid}>
    {QTYS.map((n) => (
      <button key={n} type="button" onClick={() => onPick(n)} data-testid={`${testid}-${n}`}
        className={`cx-fx-pill flex-1 rounded-full py-2 text-sm font-bold ${value === n ? "is-on" : ""}`}>{value === undefined ? `+${n}` : n}</button>
    ))}
  </div>
);

export const PlatformConnectDialog = ({ p, overlay, panel, onClose, onStarted }) => {
  const { t, lang } = useI18n();
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [qty, setQty] = useState(20);
  if (!p) return null;
  const start = async () => {
    setBusy(true);
    try {
      await api.post("/live/start", { url, platform: p.id === "youtube" ? "youtube_shorts" : "tiktok", lang, max_clips: qty });
      if (panel) window.open(panel, "_blank", "noopener");
      toast.success(t("live.started"));
      onStarted();
      onClose();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0b0e1a] border-white/10 max-w-lg" data-testid="platform-connect-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-white flex items-center gap-2"><span className="w-3 h-3 rounded-full" style={{ background: p.color }} /> {p.name}</DialogTitle>
          <DialogDescription className="text-slate-400 text-sm">{t("con.desc")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-5">
          <div>
            <div className="cx-label mb-2">1 · {t("con.overlay")}</div>
            {overlay ? <Row value={overlay} testid="connect-overlay-url" onCopy={() => { navigator.clipboard.writeText(overlay); toast.success(t("int.copied")); }} /> : <div className="text-xs text-slate-500">{t("int.restricted")}</div>}
            <p className="text-[11px] text-slate-500 mt-2">{t("con.overlay_how")}</p>
            {panel && <a href={panel} target="_blank" rel="noreferrer" data-testid="connect-panel-link" className="cx-ghost rounded-full mt-2 py-2 text-xs flex items-center justify-center gap-2"><Zap className="w-3.5 h-3.5" /> {t("int.panel_open")}</a>}
          </div>
          {p.live && (
            <div>
              <div className="cx-label mb-2">2 · {t("con.live")}</div>
              <input data-testid="connect-live-url-input" value={url} onChange={(e) => setUrl(e.target.value)} placeholder={p.ph} className="w-full rounded-lg bg-[#070912] border border-white/10 px-4 py-3 text-sm outline-none focus:border-[#2f7bff]" />
              <p className="text-[11px] text-slate-500 mt-2">{t("con.live_how")}</p>
              <div className="text-xs text-slate-300 mt-4 mb-2 font-semibold">{t("live.qty")}</div>
              <QtyPicker value={qty} onPick={setQty} testid="connect-live-qty" />
              <p className="text-[11px] text-slate-500 mt-2">{t("live.qty_hint")}</p>
              <button data-testid="connect-live-start-btn" disabled={busy || url.length < 8} onClick={start} className="cx-btn w-full mt-3 rounded-full py-3 text-sm font-semibold flex items-center justify-center gap-2">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />} {t("con.start")}
              </button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const BigClipButton = ({ t }) => {
  const [busy, setBusy] = useState(false);
  const clip = useCallback(async () => {
    setBusy(true);
    try { await api.post("/live/clip-now"); feedback("launch"); toast.success(t("live.clip_ok")); setTimeout(() => setBusy(false), 4000); }
    catch (e) { feedback("error"); toast.error(clipErr(formatApiError(e), t)); setBusy(false); }
  }, [t]);
  useClipKey(clip, !busy);
  return (
    <>
    <button onClick={clip} disabled={busy} data-testid="live-big-clip-btn"
      className="w-full mt-4 rounded-2xl py-6 text-2xl font-black tracking-widest text-white bg-gradient-to-r from-[#ff2d55] to-[#ff7a00] shadow-[0_0_40px_-8px_rgba(255,45,85,0.8)] active:scale-[0.98] transition-transform disabled:opacity-60 flex items-center justify-center gap-3">
      <Scissors className="w-7 h-7" />{busy ? t("live.clip_busy") : t("live.clip_btn")}
      <kbd className="hidden sm:inline text-xs font-mono font-bold px-2 py-0.5 rounded bg-black/30 border border-white/30">C</kbd>
    </button>
    <QuickClipAccess />
    </>
  );
};

export const LiveSessions = ({ refreshKey }) => {
  const { t } = useI18n();
  const [list, setList] = useState([]);
  const load = useCallback(() => api.get("/live").then((r) => setList(r.data)).catch(() => {}), []);
  useEffect(() => { load(); const i = setInterval(load, 8000); return () => clearInterval(i); }, [load, refreshKey]);
  const stop = async (id) => { await api.post(`/live/${id}/stop`); toast.success(t("live.stopped")); load(); };
  const more = async (id, n) => {
    try { await api.post(`/live/${id}/more-clips`, { add: n }); toast.success(t("live.more_ok", { n })); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  if (!list.length) return null;
  return (
    <div className="cx-card rounded-2xl p-5 mt-6" data-testid="live-sessions">
      <div className="cx-label flex items-center gap-2"><Radio className="w-3.5 h-3.5 text-rose-400" /> {t("live.title")}</div>
      {list.some((s) => s.status === "live") && <BigClipButton t={t} />}
      <div className="divide-y divide-white/5 mt-3">
        {list.map((s) => (
          <div key={s.id} className="flex flex-wrap items-center gap-3 py-3" data-testid="live-session-row">
            <span className={`w-2 h-2 rounded-full ${s.status === "live" ? "bg-rose-500 animate-pulse" : "bg-slate-600"}`} />
            <span className="font-mono text-xs text-slate-300 truncate max-w-[260px]">{s.url}</span>
            <span className="text-[10px] font-mono uppercase px-2 py-0.5 rounded border border-white/10 text-slate-400" data-testid="live-session-status">{t(`live.st_${s.status}`)}</span>
            <span className="text-xs text-slate-400">{t("live.stats", { s: s.segments, c: s.clips })}</span>
            {s.status === "live" && s.max_clips && <span className={`text-[10px] font-mono px-2 py-0.5 rounded border ${s.auto_paused ? "border-amber-400/40 text-amber-300 bg-amber-400/10" : "border-[#a746ff]/40 text-[#e9c6ff] bg-[#a746ff]/10"}`} data-testid="live-auto-count">{t("live.auto_count", { a: s.auto_clips || 0, m: s.max_clips })}</span>}
            {s.status === "live" && s.auto_paused && (
              <div className="basis-full flex flex-wrap items-center gap-3 rounded-xl border border-amber-400/30 bg-amber-400/[0.06] px-3 py-2.5" data-testid="live-limit-box">
                <span className="text-xs text-amber-200">{t("live.limit_hit")}</span>
                <div className="flex-1 min-w-[220px]"><QtyPicker onPick={(n) => more(s.id, n)} testid="live-more" /></div>
              </div>
            )}
            {s.status === "live" && s.chat_cmd && <span className="text-[10px] font-mono px-2 py-0.5 rounded border border-[#22e3a1]/40 text-[#22e3a1] bg-[#22e3a1]/10" title={t("live.chat_hint")} data-testid="live-chat-cmd-badge">{t("live.chat_on")}{s.chat_clip_by ? ` · ${t("live.chat_last", { u: s.chat_clip_by })}` : ""}</span>}
            {s.status === "live" && <span className="text-[10px] text-slate-500" data-testid="live-bestof-hint">{t("live.bestof_hint")}</span>}
            {s.status !== "live" && s.bestof_id && <a href={`/clips?trailer=${s.bestof_id}`} className="text-[11px] font-semibold text-[#ff9db4] hover:underline" data-testid="live-bestof-link">{t("live.bestof")}</a>}
            {s.status === "live" && s.state === "reconnecting" && <span className="text-xs text-amber-300 flex items-center gap-1" data-testid="live-reconnecting"><Loader2 className="w-3 h-3 animate-spin" />{t("live.reconnecting")}</span>}
            {s.last_error && s.state !== "reconnecting" && <span className="text-xs text-rose-400 truncate max-w-[280px]">{s.last_error}</span>}
            {s.status === "live" && <button data-testid="live-stop-btn" onClick={() => stop(s.id)} className="ml-auto cx-ghost rounded-full px-3 py-1.5 text-xs flex items-center gap-1.5"><Square className="w-3 h-3" /> {t("live.stop")}</button>}
          </div>
        ))}
      </div>
    </div>
  );
};
